import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import {
  ALL_SIDES_UNMIXED,
  COMMON_OFFSET_PRESETS,
  DEFAULT_OFFSET,
  DEFAULT_OFFSET_STRINGS,
  OFFSET_SIDES,
} from '../constants/wrap-offsets'
import type { OffsetField, OffsetKey, OffsetStrings, WrapCarrier } from '../types/wrap-offset'

export type CommonOffset = { value: number; allSame: boolean }

export function offsetValue(obj: WrapCarrier, field: OffsetField): number {
  const v = obj[field]

  return typeof v === 'number' && Number.isFinite(v) ? v : DEFAULT_OFFSET
}

/** Whether `value` is one of the shared control's preset values (so that preset highlights). */
export function isCommonOffsetPreset(value: number): boolean {
  return (COMMON_OFFSET_PRESETS as readonly number[]).includes(value)
}

/** Wrap Shape has a single underlying offset (`wrapOffsetTop`, the uniform contour offset) — its
 *  Right/Bottom/Left fields are display-only mirrors of that same value, not independent stored
 *  values, so they must always show it regardless of whatever those fields separately hold (e.g.
 *  stale values left over from a previous Bounding Box session on the same object). Jump has two
 *  real fields (Top/Bottom) but its disabled Right/Left boxes get the same display-only mirror
 *  treatment — showing `wrapOffsetBottom` — instead of whatever unrelated value happens to be
 *  stored there (e.g. left over from a previous Bounding Box session), so the panel feels
 *  consistent across every style rather than only for Wrap Shape. */
export function offsetStringsFromObj(obj: WrapCarrier, style: WrapStyle): OffsetStrings {
  if (style === 'wrap') {
    const mirrored = String(offsetValue(obj, 'wrapOffsetTop'))

    return { top: mirrored, right: mirrored, bottom: mirrored, left: mirrored }
  }

  if (style === 'jump') {
    const top = String(offsetValue(obj, 'wrapOffsetTop'))
    const bottom = String(offsetValue(obj, 'wrapOffsetBottom'))

    return { top, right: bottom, bottom, left: bottom }
  }

  const result = {} as OffsetStrings

  for (const { key, field } of OFFSET_SIDES) {
    result[key] = String(offsetValue(obj, field))
  }

  return result
}

/** The representative value shown in the shared preset/slider/input control, plus whether all
 *  four sides currently agree with it (used to highlight a matching preset). Wrap Shape only ever
 *  has one real value (`wrapOffsetTop`), so it's trivially "all same" for preset-matching purposes
 *  — Right/Bottom/Left's own stored values (display mirrors only, see `offsetStringsFromObj`) are
 *  irrelevant here. Jump only ever has two real values (`wrapOffsetTop`/`wrapOffsetBottom` — Left/
 *  Right have no effect, AC 3.2), so "all same" compares just those two. */
export function commonOffsetFromObj(obj: WrapCarrier, style: WrapStyle): CommonOffset {
  if (style === 'wrap') {
    return { value: offsetValue(obj, 'wrapOffsetTop'), allSame: true }
  }

  if (style === 'jump') {
    const top = offsetValue(obj, 'wrapOffsetTop')
    const bottom = offsetValue(obj, 'wrapOffsetBottom')

    return { value: top, allSame: top === bottom }
  }

  const values = OFFSET_SIDES.map(({ field }) => offsetValue(obj, field))
  const first = values[0] ?? DEFAULT_OFFSET

  return { value: first, allSame: values.every((v) => v === first) }
}

/** Reduces one `commonOffsetFromObj` result per selected target into what the shared control
 *  should show: a target only ever "agrees" with the group if BOTH its own sides agree with each
 *  other (its own `allSame`) AND its value matches every other target's. With exactly one target
 *  (the pre-existing single-select path) this is a no-op — `every` over a 1-element array is
 *  trivially that element's own result, so single selection is unaffected byte-for-byte. */
export function aggregateCommonOffset(perTarget: CommonOffset[]): CommonOffset {
  if (perTarget.length === 0) return { value: DEFAULT_OFFSET, allSame: true }

  const first = perTarget[0]!
  const allAgree = perTarget.every((p) => p.allSame && p.value === first.value)

  return { value: first.value, allSame: allAgree }
}

/** Reduces one `offsetStringsFromObj` result per selected target, per side, into the flat display
 *  strings + a mixed flag per side. A side is "mixed" the moment any target's own string for it
 *  disagrees with the first target's — including disagreement caused by one target not having
 *  that side enabled for its own style at all (its `offsetStringsFromObj` mirror/real value simply
 *  won't match another target's independent one). With exactly one target this always reports
 *  `allSame`, matching the pre-existing single-select behavior exactly. */
export function aggregateOffsetStrings(perTarget: OffsetStrings[]): {
  strings: OffsetStrings
  mixed: Record<OffsetKey, boolean>
} {
  const strings = { ...DEFAULT_OFFSET_STRINGS }
  const mixed = { ...ALL_SIDES_UNMIXED }

  for (const { key } of OFFSET_SIDES) {
    const values = perTarget.map((p) => p[key])
    const allSame = values.length > 0 && values.every((v) => v === values[0])

    mixed[key] = perTarget.length > 0 && !allSame
    strings[key] = allSame ? (values[0] ?? DEFAULT_OFFSET_STRINGS[key]) : ''
  }

  return { strings, mixed }
}
